import { useCallback, useMemo, useState } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { useUser } from "@components/user-context";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { formatting } from "@north-beam/nb-common";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { AboutThisSettingModal } from "../../shared/about-this-setting-modal";

export function PerformanceTrackingSetting({
  isMinimized,
}: {
  isMinimized?: boolean;
}) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const { user } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { adoptedClientSetting } = useClientSettings();
  const { kpi: adoptedKpi, spendConstraints: adoptedSpendConstraints } =
    useClientSettingData(adoptedClientSetting);
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();

  const kpi = useMemo(() => {
    let result = adoptedKpi;

    if (!result) {
      result = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      );
    }

    return result;
  }, [clientKpis, adoptedKpi, adoptedBudgetMix?.data.kpi_name]);

  const renderMinimized = useCallback(() => {
    if (isMinimized) {
      return (
        <button
          className={clsx(
            "rounded-full px-4 text-sm flex items-center gap-1.5 h-8",
            {
              "bg-v2-highlight-information hover:bg-v2-highlight-information-hover":
                !isModalOpen,
              "bg-cosmic": isModalOpen,
            },
          )}
          onClick={() => {
            trackMMMEvent({
              component:
                TrackPerformanceTrackedComponent.ABOUT_THIS_SETTING_MODAL,
              action: "onOpen",
              actionInfo: "Open from top bar",
            });

            setIsModalOpen(true);
          }}
        >
          <span
            className={clsx({
              "text-cosmic": !isModalOpen,
              "text-white": isModalOpen,
            })}
          >
            SETTING
          </span>
          <span
            className={clsx({
              "text-white": isModalOpen,
            })}
          >
            {adoptedClientSetting?.name ??
              adoptedBudgetMix?.name ??
              "Custom Budget"}
          </span>
        </button>
      );
    }
  }, [
    adoptedClientSetting?.name,
    adoptedBudgetMix?.name,
    isMinimized,
    isModalOpen,
  ]);

  if (!adoptedBudgetMix) {
    if (isMinimized) {
      return null;
    }
    return <>Adopted budget not found</>;
  }

  return (
    <div>
      {isMinimized ? (
        renderMinimized()
      ) : (
        <>
          <div className="text-xs mb-1 font-medium">SETTING</div>
          <div
            className={twMerge(
              clsx(
                "border border-v2-secondary px-3 py-1 rounded-md cursor-pointer hover:border-interstellar",
              ),
            )}
            onClick={() => {
              trackMMMEvent({
                component:
                  TrackPerformanceTrackedComponent.ABOUT_THIS_SETTING_MODAL,
                action: "onOpen",
                actionInfo: "Opened from in-page (not top bar)",
              });

              setIsModalOpen(true);
            }}
          >
            <div className="text-sm font-semibold">
              {adoptedClientSetting?.name ?? adoptedBudgetMix.name}
            </div>
            <div className="flex items-center space-x-4 text-xxs">
              <div className="flex items-center space-x-1">
                <div className="flex items-center space-x-1">
                  <div className="tracking-wide text-gray+1">KPI</div>
                  <div className="text-cosmic">{kpi?.friendly_name}</div>
                </div>
                {adoptedClientSetting && (
                  <>
                    <div className="tracking-wide text-gray+1">Budget</div>
                    <div className="flex items-center space-x-0.5">
                      <div className="text-cosmic">
                        {formatting.formatCurrency(
                          adoptedClientSetting.budget_low,
                          {
                            precision: "approximate",
                            code: user.currencies.currentCurrency,
                          },
                        )}
                      </div>
                      <div>-</div>
                      <div className="text-cosmic">
                        {formatting.formatCurrency(
                          adoptedClientSetting.budget_high,
                          {
                            precision: "approximate",
                            code: user.currencies.currentCurrency,
                          },
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {adoptedClientSetting && (
                <div className="tracking-wide ml-4">
                  <span className="mr-1 text-gray+1">Constraints</span>
                  <span className="text-cosmic">
                    {adoptedSpendConstraints.length}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <AboutThisSettingModal
        setting={{
          name:
            adoptedClientSetting?.name ??
            adoptedBudgetMix.name ??
            "Custom Budget",
          budgetLow: adoptedClientSetting?.budget_low,
          budgetHigh: adoptedClientSetting?.budget_high,
          kpi: kpi
            ? {
                name: kpi.friendly_name,
                equation: kpi.equation,
              }
            : undefined,
          spendConstraints: adoptedSpendConstraints ?? [],
        }}
        isOpen={isModalOpen}
        onClose={() => {
          trackMMMEvent({
            component:
              TrackPerformanceTrackedComponent.ABOUT_THIS_SETTING_MODAL,
            action: "onClose",
          });

          setIsModalOpen(false);
        }}
      />
    </div>
  );
}
